/**
 * Home Page - Jerusalem Gold Design
 *
 * Elegant personalized content sections with warm aesthetics.
 * Features staggered animations and refined typography.
 */

import { useEffect, useState } from 'react';
import { api } from '@/api/client';
import { SectionCarousel } from '@/components/common/SectionCarousel';
import { PageHeader } from '@/components/common/PageHeader';
import { LoadingGrid } from '@/components/common/LoadingGrid';

interface HomeSection {
  title: string;
  items: unknown[];
}

export function HomePage() {
  const [sections, setSections] = useState<HomeSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHome = async (refresh = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = refresh
        ? await api.getHome({ refresh: true })
        : await api.getHome();
      if (response.data?.sections) {
        setSections(response.data.sections as HomeSection[]);
      }
    } catch (err) {
      setError('Failed to load content');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHome();
  }, []);

  return (
    <div className="min-h-full page-enter">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-6 pb-2">
        <h1 className="text-2xl font-bold">Home</h1>
        <button
          onClick={() => loadHome(true)}
          disabled={isLoading}
          className="p-2 rounded-full hover:bg-surface-container transition-colors disabled:opacity-50"
          aria-label="Refresh"
        >
          <svg
            className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-8 space-y-10">
        {isLoading ? (
          <LoadingGrid />
        ) : error ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-container flex items-center justify-center">
              <svg className="w-8 h-8 text-on-surface-variant" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-on-surface-variant mb-4">{error}</p>
            <button
              className="btn-primary"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        ) : sections.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-container flex items-center justify-center">
              <svg className="w-8 h-8 text-on-surface-variant" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <p className="text-on-surface-variant">No content available</p>
            <p className="text-sm text-on-surface-variant/60 mt-1">Check your connection or try again later</p>
          </div>
        ) : (
          sections.map((section, index) => (
            <div
              key={index}
              className="opacity-0 animate-fade-up"
              style={{
                animationDelay: `${index * 0.15}s`,
                animationFillMode: 'forwards'
              }}
            >
              <SectionCarousel
                title={section.title}
                items={section.items}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
